import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Settings,
  KeyRound,
  Upload,
  Palette,
  Globe,
  Loader2,
  Save,
  Trash2,
  Image,
  Check,
  Building2,
} from 'lucide-react';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { PageTransition } from '../components/PageTransition';
import { changePassword } from '../services/auth.service';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

const TenantSettings: React.FC = () => {
  const { t } = useTranslation();
  const { tenant, user, updateTenant } = useAuth();
  const { showNotification } = useNotification();

  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(tenant?.primaryColor || '#f97316');
  const [secondaryColor, setSecondaryColor] = useState(tenant?.secondaryColor || '#3b82f6');
  const [language, setLanguage] = useState(tenant?.language || 'en');
  const [hasChanges, setHasChanges] = useState(false);
  const [companyAddress, setCompanyAddress] = useState(tenant?.companyAddress || '');
  const [vatNumber, setVatNumber] = useState(tenant?.vatNumber || '');
  const [pec, setPec] = useState(tenant?.pec || '');
  const [fiscalCode, setFiscalCode] = useState(tenant?.fiscalCode || '');
  const [bankName, setBankName] = useState(tenant?.bankName || '');
  const [iban, setIban] = useState(tenant?.iban || '');
  const [bic, setBic] = useState(tenant?.bic || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canManageTenantSettings = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  const handleColorChange = (type: 'primary' | 'secondary', value: string) => {
    if (type === 'primary') {
      setPrimaryColor(value);
    } else {
      setSecondaryColor(value);
    }
    setHasChanges(true);
  };

  const handleLanguageChange = (value: string) => {
    setLanguage(value);
    setHasChanges(true);
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.tenantId) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showNotification(t('settings.errors.invalidFileType', 'Invalid file type. Please upload JPEG, PNG, SVG, or WebP.'), 'error');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      showNotification(t('settings.errors.fileTooLarge', 'File too large. Maximum size is 2MB.'), 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingLogo(true);
      const response = await axios.post(`${API_URL}/tenants/${user.tenantId}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update local tenant state with new logo URL
      updateTenant({ logoUrl: response.data.logoUrl });
      showNotification(t('settings.logoUploaded', 'Logo uploaded successfully'), 'success');
    } catch (error) {
      console.error('Logo upload failed:', error);
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!user?.tenantId) return;

    try {
      setUploadingLogo(true);
      await axios.delete(`${API_URL}/tenants/${user.tenantId}/logo`);
      updateTenant({ logoUrl: null });
      showNotification(t('settings.logoDeleted', 'Logo deleted successfully'), 'success');
    } catch (error) {
      console.error('Logo delete failed:', error);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!user?.tenantId || !canManageTenantSettings) return;

    try {
      const payload: Record<string, string> = {};
      const currentPrimaryColor = tenant?.primaryColor || '#f97316';
      const currentSecondaryColor = tenant?.secondaryColor || '#3b82f6';
      const currentLanguage = tenant?.language || 'en';

      if (primaryColor !== currentPrimaryColor) payload.primaryColor = primaryColor;
      if (secondaryColor !== currentSecondaryColor) payload.secondaryColor = secondaryColor;
      if (language !== currentLanguage) payload.language = language;

      const addChangedTextField = (key: string, nextValue: string, currentValue: string | null | undefined) => {
        const normalizedNext = nextValue.trim();
        const normalizedCurrent = (currentValue || '').trim();

        if (normalizedNext === normalizedCurrent) {
          return;
        }

        // Avoid sending empty optional fields that may fail backend validators.
        if (!normalizedNext) {
          return;
        }

        payload[key] = normalizedNext;
      };

      addChangedTextField('companyAddress', companyAddress, tenant?.companyAddress);
      addChangedTextField('vatNumber', vatNumber, tenant?.vatNumber);
      addChangedTextField('pec', pec, tenant?.pec);
      addChangedTextField('fiscalCode', fiscalCode, tenant?.fiscalCode);
      addChangedTextField('bankName', bankName, tenant?.bankName);
      addChangedTextField('iban', iban, tenant?.iban);
      addChangedTextField('bic', bic, tenant?.bic);

      if (Object.keys(payload).length === 0) {
        setHasChanges(false);
        return;
      }

      setLoading(true);
      await axios.patch(`${API_URL}/tenants/${user.tenantId}`, payload);

      // Update local tenant state
      updateTenant(payload);
      setHasChanges(false);
      showNotification(t('settings.saved', 'Settings saved successfully'), 'success');
    } catch (error) {
      console.error('Save settings failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = () => {
    if (!tenant?.logoUrl) return null;
    // Handle both relative and absolute URLs
    if (tenant.logoUrl.startsWith('http')) {
      return tenant.logoUrl;
    }
    return `${API_URL}${tenant.logoUrl}`;
  };

  const logoUrl = getLogoUrl();

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showNotification(t('settings.password.errors.required', 'All password fields are required'), 'error');
      return;
    }

    if (newPassword.length < 8) {
      showNotification(t('settings.password.errors.minLength', 'New password must be at least 8 characters'), 'error');
      return;
    }

    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      showNotification(t('settings.password.errors.requirements', 'Password does not meet minimum requirements'), 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showNotification(t('settings.password.errors.mismatch', 'New password and confirmation do not match'), 'error');
      return;
    }

    try {
      setPasswordLoading(true);
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showNotification(t('settings.password.changed', 'Password changed successfully'), 'success');
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message;
      if (typeof backendMessage === 'string' && backendMessage.toLowerCase().includes('current password is incorrect')) {
        showNotification(t('settings.password.errors.wrongCurrent', 'Current password is incorrect'), 'error');
      } else {
        showNotification(t('settings.password.errors.changeFailed', 'Failed to change password'), 'error');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <PageTransition className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-1"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <Settings className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {t('settings.title', 'Tenant Settings')}
            </h1>
            <p className="text-muted-foreground">
              {t('settings.subtitle', 'Customize your organization\'s branding and preferences')}
            </p>
          </div>
        </div>
      </motion.div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-orange-500" />
            {t('settings.password.title', 'Password')}
          </CardTitle>
          <CardDescription>
            {t('settings.password.description', 'Change your account password')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">{t('settings.password.current', 'Current password')}</Label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('settings.password.new', 'New password')}</Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t('common.passwordRequirements')}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('settings.password.confirm', 'Repeat new password')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleChangePassword} disabled={passwordLoading}>
              {passwordLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4 mr-2" />
              )}
              {t('settings.password.submit', 'Change Password')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {canManageTenantSettings && (
      <div className="grid gap-6 md:grid-cols-2">
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-4 w-4 text-orange-500" />
                {t('settings.logo.title', 'Company Logo')}
              </CardTitle>
              <CardDescription>
                {t('settings.logo.description', 'Upload your company logo (JPEG, PNG, SVG, or WebP, max 2MB)')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Preview */}
              <div className="flex items-center justify-center p-6 bg-muted rounded-lg min-h-[150px]">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={t('settings.logo.alt', 'Company logo')}
                    className="max-h-[120px] max-w-full object-contain"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('settings.logo.noLogo', 'No logo uploaded')}</p>
                  </div>
                )}
              </div>

              {/* Upload/Delete Buttons */}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="flex-1"
                >
                  {uploadingLogo ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {t('settings.logo.upload', 'Upload Logo')}
                </Button>
                {logoUrl && (
                  <Button
                    variant="outline"
                    onClick={handleDeleteLogo}
                    disabled={uploadingLogo}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Colors Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-orange-500" />
                {t('settings.colors.title', 'Brand Colors')}
              </CardTitle>
              <CardDescription>
                {t('settings.colors.description', 'Customize the color scheme for your organization')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Primary Color */}
              <div className="space-y-2">
                <Label htmlFor="primaryColor">{t('settings.colors.primary', 'Primary Color')}</Label>
                <div className="flex gap-3 items-center">
                  <div className="relative">
                    <input
                      type="color"
                      id="primaryColor"
                      value={primaryColor}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                    />
                  </div>
                  <Input
                    value={primaryColor}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                    placeholder="#f97316"
                    className="flex-1 font-mono"
                    maxLength={7}
                  />
                  <div
                    className="w-12 h-12 rounded-lg border-2 border-border"
                    style={{ backgroundColor: primaryColor }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('settings.colors.primaryHint', 'Used for buttons, links, and accents')}
                </p>
              </div>

              <Separator />

              {/* Secondary Color */}
              <div className="space-y-2">
                <Label htmlFor="secondaryColor">{t('settings.colors.secondary', 'Secondary Color')}</Label>
                <div className="flex gap-3 items-center">
                  <div className="relative">
                    <input
                      type="color"
                      id="secondaryColor"
                      value={secondaryColor}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-border"
                    />
                  </div>
                  <Input
                    value={secondaryColor}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                    placeholder="#3b82f6"
                    className="flex-1 font-mono"
                    maxLength={7}
                  />
                  <div
                    className="w-12 h-12 rounded-lg border-2 border-border"
                    style={{ backgroundColor: secondaryColor }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('settings.colors.secondaryHint', 'Used for secondary elements and highlights')}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Language Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-orange-500" />
                {t('settings.language.title', 'Default Language')}
              </CardTitle>
              <CardDescription>
                {t('settings.language.description', 'Set the default language for your organization')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  variant={language === 'en' ? 'default' : 'outline'}
                  onClick={() => handleLanguageChange('en')}
                  className="flex-1"
                >
                  {language === 'en' && <Check className="h-4 w-4 mr-2" />}
                  English
                </Button>
                <Button
                  variant={language === 'it' ? 'default' : 'outline'}
                  onClick={() => handleLanguageChange('it')}
                  className="flex-1"
                >
                  {language === 'it' && <Check className="h-4 w-4 mr-2" />}
                  Italiano
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Billing Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.35 }}
          className="md:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-orange-500" />
                {t('settings.billing.title', 'Billing Information')}
              </CardTitle>
              <CardDescription>
                {t('settings.billing.description', 'Company details')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Company Address */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="companyAddress">{t('settings.billing.companyAddress', 'Company Address')}</Label>
                  <Input
                    id="companyAddress"
                    value={companyAddress}
                    onChange={(e) => { setCompanyAddress(e.target.value); setHasChanges(true); }}
                    placeholder={t('settings.billing.companyAddressPlaceholder', 'Street, City, ZIP, Country')}
                  />
                </div>

                {/* VAT Number */}
                <div className="space-y-2">
                  <Label htmlFor="vatNumber">{t('settings.billing.vatNumber', 'VAT Number (P.IVA)')}</Label>
                  <Input
                    id="vatNumber"
                    value={vatNumber}
                    onChange={(e) => { setVatNumber(e.target.value); setHasChanges(true); }}
                    placeholder="IT12345678901"
                    className="font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pec">{t('settings.billing.pec', 'PEC')}</Label>
                  <Input
                    id="pec"
                    type="email"
                    value={pec}
                    onChange={(e) => { setPec(e.target.value); setHasChanges(true); }}
                    placeholder="azienda@pec.it"
                  />
                </div>

                {/* Fiscal Code */}
                <div className="space-y-2">
                  <Label htmlFor="fiscalCode">{t('settings.billing.fiscalCode', 'Fiscal Code')}</Label>
                  <Input
                    id="fiscalCode"
                    value={fiscalCode}
                    onChange={(e) => { setFiscalCode(e.target.value); setHasChanges(true); }}
                    placeholder="RSSMRA85M01H501Z"
                    className="font-mono"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-3">
                {/* Bank Name */}
                <div className="space-y-2">
                  <Label htmlFor="bankName">{t('settings.billing.bankName', 'Bank Name')}</Label>
                  <Input
                    id="bankName"
                    value={bankName}
                    onChange={(e) => { setBankName(e.target.value); setHasChanges(true); }}
                    placeholder={t('settings.billing.bankNamePlaceholder', 'Bank name')}
                  />
                </div>

                {/* IBAN */}
                <div className="space-y-2">
                  <Label htmlFor="iban">{t('settings.billing.iban', 'IBAN')}</Label>
                  <Input
                    id="iban"
                    value={iban}
                    onChange={(e) => { setIban(e.target.value); setHasChanges(true); }}
                    placeholder="IT60X0542811101000000123456"
                    className="font-mono"
                  />
                </div>

                {/* BIC/SWIFT */}
                <div className="space-y-2">
                  <Label htmlFor="bic">{t('settings.billing.bic', 'BIC/SWIFT')}</Label>
                  <Input
                    id="bic"
                    value={bic}
                    onChange={(e) => { setBic(e.target.value); setHasChanges(true); }}
                    placeholder="ABCDITMMXXX"
                    className="font-mono"
                  />
                </div>
              </div>

            </CardContent>
          </Card>
        </motion.div>

        {/* Preview Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.preview.title', 'Preview')}</CardTitle>
              <CardDescription>
                {t('settings.preview.description', 'See how your branding will look')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 rounded-lg border bg-card space-y-4">
                {/* Sample header */}
                <div className="flex items-center gap-3 pb-3 border-b">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Preview" className="h-8 object-contain" />
                  ) : (
                    <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                      <Image className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <span className="font-semibold">{tenant?.name || 'Your Company'}</span>
                </div>

                {/* Sample buttons */}
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {t('settings.preview.primaryButton', 'Primary Action')}
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: secondaryColor }}
                  >
                    {t('settings.preview.secondaryButton', 'Secondary')}
                  </button>
                </div>

                {/* Sample text */}
                <p className="text-sm text-muted-foreground">
                  {t('settings.preview.text', 'This is how text elements will appear with your brand colors.')}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
      )}

      {/* Save Button */}
      {canManageTenantSettings && hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-end [&>div]:w-full sm:[&>div]:w-auto"
        >
          <Alert className="flex flex-col gap-3 bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm">
              {t('settings.unsavedChanges', 'You have unsaved changes')}
            </span>
            <Button onClick={handleSaveSettings} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {t('settings.save', 'Save Changes')}
            </Button>
          </Alert>
        </motion.div>
      )}
    </PageTransition>
  );
};

export default TenantSettings;
